import React, { useCallback, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, Checkbox, IconButton, Stack, Typography } from "@mui/material";
import { WorkspaceIcon } from "assets/material-custom-icons";
import { ISaveDealAsTemplateData } from "store/types/templates";

import { TemplateContentFolder } from "./TemplateContentFolder";
import { useTemplateContentSelection } from "./use-template-content-selection";

interface Props {
  onSubmit: (sections: ISaveDealAsTemplateData["sections"]) => void;
  onCancel: () => void;
}

export const SelectDealContentTemplate: React.FC<Props> = ({ onSubmit, onCancel }) => {
  const {
    state,
    isSelectedAll,
    toggleFolder,
    toggleItem,
    toggleAll,
    isSelected,
    buildPayload,
    folders,
    files,
    forms,
    documents
  } = useTemplateContentSelection();

  const parentFolders = useMemo(() => {
    return folders.filter((folder) => !folder.parent_id);
  }, [folders]);

  const handleSubmit = useCallback(() => {
    if (!state.folders.length) return;
    onSubmit(buildPayload());
  }, [state.folders.length, buildPayload, onSubmit]);

  return (
    <Stack
      sx={{
        padding: "32px",
        borderRadius: "8px",
        backgroundColor: "#fff",
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "60vw"
      }}
      gap="20px"
      justifyContent="space-between"
    >
      <Typography
        variant="h6"
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid  #DAE0E2"
        }}
      >
        Select Content for Template
        <IconButton onClick={onCancel}>
          <CloseIcon />
        </IconButton>
      </Typography>

      <Stack>
        <Stack
          sx={{
            border: "1px solid #DBE8F5",
            height: "49vh",
            overflow: "auto",
            marginBottom: "10px"
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            sx={{ height: "40px", minHeight: "40px", borderBottom: "1px solid #DAE0E2", width: "100%" }}
          >
            <Stack direction="row" alignItems="center" gap="4px">
              <Checkbox checked={isSelectedAll} onChange={toggleAll} size="small" sx={{ padding: "4px" }} />
              <WorkspaceIcon sx={{ fill: "#0A3142" }} />
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#0a3142",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden"
                }}
              >
                Workspace
              </Typography>
            </Stack>
          </Stack>
          {parentFolders.map((folder) => (
            <TemplateContentFolder
              key={folder.id}
              folder={folder}
              allFolders={folders}
              files={files}
              forms={forms}
              documents={documents}
              isFolderSelected={(id) => isSelected("folders", id)}
              isFileSelected={(id) => isSelected("files", id)}
              isFormSelected={(id) => isSelected("forms", id)}
              isDocumentSelected={(id) => isSelected("documents", id)}
              onToggleFolder={toggleFolder}
              onToggleFile={(item) => toggleItem("files", item)}
              onToggleForm={(item) => toggleItem("forms", item)}
              onToggleDocument={(item) => toggleItem("documents", item)}
            />
          ))}
        </Stack>
      </Stack>

      <Stack flexDirection="row" alignItems="center" justifyContent="flex-end" gap="10px">
        <Button variant="outlined" sx={{ minWidth: "53px" }} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          disabled={!state.folders.length}
          variant="contained"
          sx={{ backgroundColor: "#071F35", minWidth: "46px" }}
          onClick={handleSubmit}
        >
          Select
        </Button>
      </Stack>
    </Stack>
  );
};
